<template>
  <div :id="'model-live-box-' + video.id" class="video-container">
    <ClientOnly>
      <vue-plyr ref="plyr" :options="plyrOptions">
        <video :autoplay="false" controls playsinline ref="videoRef">
          <source src="" type="video/mp4" />
        </video>
      </vue-plyr>
      <div v-if="showIcon" class="play-icon" @click="playVideo">
        <font-awesome :icon="['fas', 'circle-play']" size="3x" />
      </div>
      <i class="watermark">{{ storeUser.user.id }}</i>
      <button class="fullscreen-button" @click="toggleFullscreen()">
        <i class="icon-resize-full"></i>
      </button>
    </ClientOnly>
  </div>
</template>

<script lang="ts" setup>
import { gtmViewVideo, gtmPlayVideo, gtmFinishVideo } from "~/util/gtm-videos";
import VuePlyr from "vue-plyr";
import Hls from "hls.js";
import { hlsConfigComCredenciais } from "~/util/hls-config";
import "vue-plyr/dist/vue-plyr.css";
import translateModel from "@/tradutors/model";

const translate = computed(() =>
  route.params.language == "pt-br"
    ? translateModel.portugues
    : translateModel.ingles
);

const plyr = ref<HTMLVideoElement | null>(null);

const hls = new Hls(hlsConfigComCredenciais);

const props = defineProps(["video", "thumbVideo"]);
const emits = defineEmits(["showModalMessages"]);

useFullscreenLandscape();

const videoSrc = ref("");
const videoRef = ref<HTMLVideoElement | null>(null);
const interval = ref<any>(null);
const showIcon = ref(true);

const plyrOptions = ref({
  controls: [
    "play-large", // The large play button in the center
    "restart", // Restart playback
    "rewind", // Rewind by the seek time (default 10 seconds)
    "play", // Play/pause playback
    "fast-forward", // Fast forward by the seek time (default 10 seconds)
    "progress", // The progress bar and scrubber for playback and buffering
    "current-time", // The current time of playback
    "duration", // The full duration of the media
    "mute", // Toggle mute
    "volume", // Volume control
    "captions", // Toggle captions
    "settings",
  ],
  fullscreen: {
    enabled: true,
    iosNative: true,
  },
  settings: ["quality", "speed"],
  debug: false,
  quality: {
    options: [],
    forced: true,
    default: 360,
  },
});
const isVisible = ref(false);
const isInPictureInPictureEnabled = ref(false);
const storeJwt = jwtStore();
const route = useRoute();
const storeUser = userStore();

watch(
  () => props.video,
  async () => {
    if (props.video) {
      showIcon.value = true;        
      videoSrc.value = await getVideo();
    }
  },
  { immediate: true }
);

watch(
  () => videoSrc.value,
  () => {
    if (videoSrc.value) {
      if (videoRef.value) {
        videoRef.value.src = videoSrc.value;
        videoRef.value.poster = props.thumbVideo ?? '';
        if (plyr.value?.player) {
          plyr.value.player.poster = props.thumbVideo ?? '';
        }
      }
    }
  }
);

watch(
  () => props.thumbVideo,
  (newThumb) => {
    const poster = newThumb ?? '';
    if (videoRef.value) videoRef.value.poster = poster;
    if (plyr.value?.player) plyr.value.player.poster = poster;
  }
);

watch(videoRef, () => {
  if (plyr.value) {
    plyr.value.player.on("ready", () => {
      gtmViewVideo("making-of", "completo", route.params.name as string);
      showIcon.value = true;      
    });
    plyr.value.player.on("play", () => {
      gtmPlayVideo("making-of", "completo", route.params.name as string);
      showIcon.value = false;
    });
    plyr.value.player.on("ended", () => {
      gtmFinishVideo("making-of", "completo", route.params.name as string);
      showIcon.value = true;
    });
    if (videoRef.value) {
      videoRef.value.addEventListener("enterpictureinpicture", () => {
        isInPictureInPictureEnabled.value = true;
        videoRef.value.src = videoSrc.value = "";
        validaFirefox();
      });
      videoRef.value.addEventListener("leavepictureinpicture", async () => {
        isInPictureInPictureEnabled.value = false;
        videoSrc.value = await getVideo();
      });
      videoRef.value.poster = props.thumbVideo ?? '';
      plyr.value.player.poster = props.thumbVideo ?? '';
    }
  }
});

onMounted(async () => {
  videoSrc.value = await getVideo();
  checkVisibility();
  interval.value = setInterval(checkVisibility, 1000);
});
onUnmounted(() => {
  clearInterval(interval.value);

  if (hls) {
    hls.destroy();
  }
});
async function getVideo() {
  let res = props.video.livePath
    ? props.video.livePath // -> making-of
        .replace("storage/essay-live-videos", "api/v1/stream/livesHls")
        .replace(".mp4", ".xyd") +
      (storeJwt.jwt ? `&hash=${storeJwt.jwt}` : "a")
    : props.video.videoPath; // -> lives

  if (!res) return "";    
  
  let videosUrl;
  if (props.video.videoPath) {
    videosUrl = { video: props.video.videoPath };
  } else {
    // credentials: "include" e obrigatorio aqui, pelo mesmo motivo do
    // BoxVideo.vue: a pagina roda em belladasemana.com.br e a API responde
    // de api.belladasemana.com.br. Sem isso o navegador DESCARTA o Set-Cookie
    // da resposta, e o cracha nunca chega a existir neste fluxo.
    //
    // Medido em producao (14/08/2026) pelo campo uid no log do nginx, ja com
    // o backend emitindo o cracha em apiLivesHls:
    //   stream/livesHls  (sem credentials)    0 com cookie / 450 sem
    //   stream/videosHls (com credentials)  212 com cookie
    // O zero e a prova: nao era adesao baixa, era o cookie sendo descartado.
    //
    // Sem esta correcao o making-of (~21 mil requisicoes/dia) responde 403 no
    // instante em que o nginx passar a exigir o cracha.
    videosUrl = await $fetch(res, { credentials: "include" });
  }

  if (Hls.isSupported() && videoRef.value) {
    hls.loadSource(videosUrl.video);
    hls.attachMedia(videoRef.value);
    return "";
  } else if (
    videoRef.value &&
    videoRef.value.canPlayType("application/vnd.apple.mpegurl")
  ) {
    return videosUrl.video;
  }

  return videosUrl.videoNotHls;
}
function validaFirefox() {
  if (!videoSrc.value && !isInPictureInPictureEnabled.value) {
    let msg =
      route.params.language === "pt-br"
        ? "Momentaneamente não é possível acessar os vídeos do Bella da Semana. Por favor, acesse através de outro navegador. Agradecemos a sua compreensão!"
        : "At the moment it is not possible to access Bella Club videos. Please, use an alternative navigator in order to access. We appreciate your understanding!";
    emits("showModalMessages", {
      message: {
        show: true,
        title: "Error",
        textBoby: msg,
        textButton: route.params.language === "pt-br" ? "Fechar" : "Close",
      },
    });
  } else if (!videoSrc.value && isInPictureInPictureEnabled.value) {
    let msg =
      route.params.language === "pt-br"
        ? "Momentaneamente não é possível acessar as lives do Bella da Semana com o picture in picture ativado. Por favor, desative e recarregue a página. Agradecemos a sua compreensão!"
        : "At the moment it is not possible to access Bella Club lives using picture in picture. Please, disable and reload site. We appreciate your understanding!";
    emits("showModalMessages", {
      message: {
        show: true,
        title: "Error",
        textBoby: msg,
        textButton: route.params.language === "pt-br" ? "Fechar" : "Close",
      },
    });
  }
}

function checkVisibility() {
  const icon: any = document.querySelector(".watermark");
  isVisible.value = icon && icon.offsetParent !== null;

  let computedStyle = icon ? window.getComputedStyle(icon) : null;
  let animationName = computedStyle ? computedStyle.animationName : null;

  if (
    !isVisible.value ||
    animationName == "none" ||
    computedStyle?.animationDuration != "15s" ||
    computedStyle.animationIterationCount != "infinite"
  )
    videoSrc.value = "";
  if (icon) {
    icon.style =
      "position: absolute;left: 3%;top: 3%;color: #cccccc !important;opacity: 0.2 !important;visibility: visible !important;font-size: 1rem !important";
    icon.class = "watermark";
  }
}

function iOS() {
  return [
    "iPad Simulator",
    "iPhone Simulator",
    "iPod Simulator",
    "iPad",
    "iPhone",
    "iPod",
  ].includes(navigator?.userAgentData?.platform || navigator?.platform);
}
function firefox() {
  const test1 = navigator?.userAgent.includes("Firefox");
  // teste dois verifica se o navigator.vendor vem vazio, o que é característico do Firefox
  const test2 = navigator?.vendor === "";
  // teste 3 typeof InstallTrigger !== 'undefined'
  const test3 = typeof InstallTrigger !== "undefined";
  // Verificando a presença de um prefixo CSS exclusivo do Firefox
  const test4 = CSS.supports("(-moz-user-focus: normal)");
  return test1 || test2 || test3 || test4;
}
function toggleFullscreen() {
  if (!iOS()) {
    if (!document.fullscreenElement) {
      const videoBox = document.getElementById(
        "model-live-box-" + props.video.id
      );
      if (videoBox) {
        videoBox.requestFullscreen();
        videoBox.classList.toggle("full");
      }
    } else if (document.exitFullscreen) {
      const videoBox = document.getElementById(
        "model-live-box-" + props.video.id
      );
      if (videoBox) {
        videoBox.classList.toggle("full");
      }
      document.exitFullscreen();
    }
  } else {
    const videoBox = document.getElementById(
      "model-live-box-" + props.video.id
    );
    if (videoBox) {
      videoBox.classList.toggle("video-container-ios");
    }
  }
}

function playVideo() {
  if (plyr.value) {
    plyr.value.player.play();
  }
}

// Para as tools WebMCP do player (MakingOf.vue): o Plyr, para play, pausa e tempo.
defineExpose({ player: () => (plyr.value as any)?.player ?? null });
</script>

<style scoped>
.full >>> .plyr video {
  max-height: 100vh !important;
}

.plyr video {
  width: 100%;  
  aspect-ratio: 16 / 9;
  object-fit: contain;
}

.plyr:fullscreen video {
    width: 100vw !important;
    aspect-ratio: 16 / 9;
    object-fit: contain;
}

.video-container {
  position: relative;
}

.video-container-ios {
  position: fixed; /* Fixa o contêiner na tela */
  top: 0;
  left: 0;
  width: 100vw; /* Largura total da viewport */
  height: 100vh; /* Altura total da viewport */
  background-color: black; /* Fundo preto para simular fullscreen */
  z-index: 9999; /* Coloca o contêiner acima de outros elementos */
}

.video-container-ios iframe {
  height: 100vh;
}

.full {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.full iframe {
  height: 100vh;
}

.fullscreen-button {
  position: absolute;
  top: 3%;
  right: 1%;
  z-index: 10;
}

.watermark {
  position: absolute;
  left: 3%;
  top: 3%;
  color: #cccccc;
  opacity: 0.2;
  visibility: visible !important;
  animation: 15s moving-watermark infinite;
}

.play-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  /* Tamanho do ícone de reprodução */
  color: white;
  /* Cor do ícone de reprodução */
  cursor: pointer;
  z-index: 1;
}

@keyframes moving-watermark {
  0% {
    left: 3%;
    top: 50%;
  }

  25% {
    left: 45%;
    top: 3%;
  }

  50% {
    left: 45%;
    top: 90%;
  }

  75% {
    left: 93%;
    top: 50%;
  }

  100% {
    left: 3%;
    top: 50%;
  }
}

i[class*="icon-"]:before {
  color: #ffffff;
  font-size: 25px;
}

@media screen and (max-width: 970px) {
  .video-container {
    width: 98vw;
  }
  .video-container-ios {
    width: 100vw;
  }
  video {
    height: 100%;
  }
  .plyr video {
    aspect-ratio: 16 / 9; 
  }

  @keyframes moving-watermark {
    0% {
      left: 3%;
      top: 50%;
    }
    25% {
      left: 45%;
      top: 3%;
    }
    50% {
      left: 45%;
      top: 85%;
    }
    75% {
      left: 85%;
      top: 50%;
    }
    100% {
      left: 3%;
      top: 50%;
    }
  }

}
</style>
